import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Nyköping",
    text: "Vi planerar leveransen från Eskilstuna till Nyköping utifrån detaljernas omfattning, önskat datum och vald transportlösning.",
  },
  {
    icon: Clock,
    title: "Korta ledtider",
    text: "Tack vare tre CO2-lasrar och hög kapacitet kan vi hantera brådskande beställningar från Nyköpingsregionen med kort varsel.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning",
    text: "Kombinera laserskärning med kantpressning och svetsning – allt under ett tak. Färdiga komponenter levererade till Nyköping.",
  },
];

const nykopingIndustries = [
  {
    name: "Flygindustri & Logistik",
    desc: "Med närheten till Stockholm Skavsta Airport är vi en strategisk partner för företag inom flygunderhåll, logistik och transport.",
  },
  {
    name: "Försvarsindustri",
    desc: "Vi har erfarenhet av att leverera komponenter med höga krav på precision och materialkvalitet till försvarsrelaterade företag i regionen.",
  },
  {
    name: "Tillverkningsindustri",
    desc: "ASPAB är en komplett leverantör för tillverkande företag i Nyköping, från prototyper till storskalig serieproduktion av metalldetaljer.",
  },
  {
    name: "Bygg & Entreprenad",
    desc: "Vi tillverkar och levererar skräddarsydda byggnadselement, konstruktionsdetaljer och inredning i metall till byggprojekt i Nyköping.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningNykoping() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Nyköping",
    description:
      "Professionell laserskärning för företag i Nyköping och Södermanland. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Nyköping",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Nyköping",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Nyköping och Södermanland. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Nyköping – Lokal service, snabb leverans | ASPAB"
        description="Behöver du laserskärning i Nyköping? ASPAB i Eskilstuna levererar med 0,1 mm tolerans. Stål, rostfritt & aluminium. Begär offert för snabb service!"
        canonical="https://aspab.com/laserskarning-nykoping"
        path="/laserskarning-nykoping"
        keywords="laserskärning Nyköping, laserskärning Södermanland, plåtbearbetning Nyköping, CO2 Laser Nyköping, metallbearbetning Nyköping"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Nyköping", href: "/laserskarning-nykoping" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogHem}
      />

      <PageHero
        title="Laserskärning Nyköping"
        subtitle="Effektiv och precis laserskärning för Nyköpings industri. ASPAB i Eskilstuna är din pålitliga partner för metalldetaljer med snabb leverans."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Nyköping", href: "/laserskarning-nykoping" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Nyköping */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Nyköpingsregionen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Partner för laserskärning i Nyköping
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Letar ditt företag i Nyköping efter en specialist på laserskärning? ASPAB i Eskilstuna kombinerar hög kapacitet med planerade transporter till hela Nyköpingsområdet.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vår expertis inom CO2-laserskärning och vår moderna maskinpark med tre lasrar från Trumpf och Amada gör oss till en idealisk partner för allt från prototyptillverkning till storskalig serieproduktion. Vi är vana att arbeta med kunder inom krävande branscher som flyg, försvar och tillverkningsindustri.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Nyköping</span>
                    <span className="text-gray-500 text-sm block">Via väg 53 – effektiva leveranser</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – få snabb återkoppling <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Nyköping – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">1.5h</div>
                    <div className="text-gray-500 text-sm mt-1">Transporttid</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">3 st</div>
                    <div className="text-gray-500 text-sm mt-1">CO2-lasrar</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Nyköpingsföretag */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför företag i Nyköping väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Trots avståndet erbjuder vi en service som konkurrerar med lokala alternativ tack vare vår höga kapacitet, specialistkompetens och effektiva logistik.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB] mb-4" />
                  <h3 className="font-heading text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Maskinpark & Kapacitet */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Kapacitet för Nyköping
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Tre CO2-lasrar för maximal flexibilitet
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vår maskinpark består av tre moderna CO2-lasermaskiner från Trumpf och Amada. Detta ger oss en oöverträffad kapacitet och flexibilitet att hantera både små och stora beställningar från Nyköping med omnejd. Vi kan skära i ett brett spektrum av material och tjocklekar med högsta precision.
                </p>
                <div className="mt-8 space-y-4">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <Zap className="w-6 h-6 text-[#2563EB] shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">{cap.material}</p>
                        <p className="text-sm text-gray-500">
                          Tjocklek: {cap.minThickness} – {cap.maxThickness}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.closeup1}
                alt="CO2-laserskärning av stålplåt för kund i Nyköping"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Industrier i Nyköping */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Lokala industrier
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Leverantör till nyckelindustrier i Nyköping
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vi är stolta över att vara en strategisk partner till flera av de viktigaste industrierna i Nyköpingsregionen. Vår förmåga att leverera högkvalitativa komponenter med kort varsel är avgörande för våra kunders framgång.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {nykopingIndustries.map((industry, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <h3 className="font-heading text-lg font-bold text-gray-900">{industry.name}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{industry.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
              Redo att starta ditt projekt i Nyköping?
            </h2>
            <p className="text-gray-500 mt-4 text-lg max-w-2xl mx-auto">
              Kontakta oss idag för en kostnadsfri offert. Ladda upp dina ritningar (STEP, DXF, DWG) så återkommer vi med pris och beräknad leveranstid när underlaget är granskat.
            </p>
            <Link
              href="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-8"
            >
              Begär offert för laserskärning <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
